import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useStoreClubAuth } from '../../contexts/StoreClubAuthContext';
import { PrayerRequestButton } from '../Prayer';

interface ClubLayoutProps {
  children: React.ReactNode;
  hideNav?: boolean;
}

const ClubLayout: React.FC<ClubLayoutProps> = ({ children, hideNav = false }) => {
  const location = useLocation();
  const { member } = useStoreClubAuth();

  const isActive = (path: string) => location.pathname === path;

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <Link to="/club/dashboard" style={styles.logoLink}>
          <img src="/rbf-logo.png" alt="Red Bridge Farm" style={styles.logo} />
          <span style={styles.title}>Store Club</span>
        </Link>
      </header>

      <main style={styles.main}>
        {children}

        {/* Prayer Request Button - Footer style, scrolls with page */}
        {member && (
          <PrayerRequestButton
            portalSource="store_club"
            userName={member.name}
            userEmail={member.email}
            submitEndpoint="/store-club/prayer-request/"
          />
        )}
      </main>

      {!hideNav && (
        <nav style={styles.bottomNav}>
          <Link
            to="/club/dashboard"
            style={{
              ...styles.navItem,
              color: isActive('/club/dashboard') ? '#E73C3E' : '#666',
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            <span style={styles.navLabel}>Home</span>
          </Link>

          <Link
            to="/club/shop"
            style={{
              ...styles.navItem,
              color: isActive('/club/shop') ? '#E73C3E' : '#666',
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
            <span style={styles.navLabel}>Shop</span>
          </Link>

          <Link
            to="/club/orders"
            style={{
              ...styles.navItem,
              color: isActive('/club/orders') ? '#E73C3E' : '#666',
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
              <line x1="12" y1="22.08" x2="12" y2="12" />
            </svg>
            <span style={styles.navLabel}>Orders</span>
          </Link>

          <Link
            to="/club/farm"
            style={{
              ...styles.navItem,
              color: isActive('/club/farm') ? '#E73C3E' : '#666',
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
              <path d="M8 14s1.5 2 4 2 4-2 4-2" />
              <line x1="9" y1="9" x2="9.01" y2="9" />
              <line x1="15" y1="9" x2="15.01" y2="9" />
            </svg>
            <span style={styles.navLabel}>Farm</span>
          </Link>
        </nav>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    backgroundColor: '#fff',
    padding: '0.75rem 1rem',
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid #e0e0e0',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  logoLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    textDecoration: 'none',
  },
  logo: {
    width: '40px',
    height: 'auto',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#E73C3E',
    fontFamily: "'Open Sans', sans-serif",
  },
  main: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingBottom: '80px', // Space for fixed bottom nav
    WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
  },
  bottomNav: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    padding: '0.5rem 0 0.75rem',
    borderTop: '1px solid #e0e0e0',
    zIndex: 100,
  },
  navItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.25rem',
    textDecoration: 'none',
    padding: '0.25rem 0.75rem',
    transition: 'color 0.2s',
  },
  navLabel: {
    fontSize: '0.6875rem',
    fontWeight: 500,
    fontFamily: "'Open Sans', sans-serif",
  },
};

export default ClubLayout;
